import { moment, React } from 'Imports';
import classNames from 'classnames';
import { isEncompass } from '$Utilities/integrationPlatform';
import VPButton from '$Components/Shared/Button/VPButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { faVideoSlash } from '@fortawesome/pro-regular-svg-icons';
import { DeviceAssociationService } from '$State/DeviceAssociationFreezerService';
import { CameraDeviceSegment, VehicleCameraPairingResponse } from '$Generated/api';
import * as DateFormatter from '$Components/Shared/DateFormatter';

const styles = require('./FootageSegments.scss') as {
    geotab: string;
    encompass: string;
    container: string;
    loadingContainer: string;
    noFootageContainer: string;
    availableFootageContainer: string;
    segmentItem: string;
    segmentItemMergesWithPrevious: string;
    segmentItemMergesWithNext: string;
    segmentItemSpacedBelow: string;
    segmentName: string;
    segmentGroupPosition: string;
    segmentTime: string;
    title: string;
    description: string;
    descriptionAndTitleWrapper: string;
    titleAvailableSegments: string;
};

export type FootageViewState = 'idle' | 'searching' | 'no-footage' | 'results';

export type CameraSegmentBase = CameraDeviceSegment & { serialNumber: string; segmentTitle: string };
export type CameraSegmentFullData = CameraSegmentBase & { groupSegments: CameraSegmentBase[] };

interface FootageSegmentsProps {
    date: Date;
    pairing: VehicleCameraPairingResponse;
    viewState: FootageViewState;
    availableSegments: CameraDeviceSegment[];
    onViewStateChange: (state: FootageViewState) => void;
    onSegmentsChange: (segments: CameraDeviceSegment[]) => void;
    onSegmentSelected?: (segment: CameraSegmentFullData) => void;
}

interface SegmentDisplayInfo {
    mergesWithPrevious: boolean;
    mergesWithNext: boolean;
    groupNumber: number;
    positionInGroup: number;
    groupSize: number;
}

// Segments with no gap between them (one ends exactly when the next starts) are rendered flush against each
// other as a single visual block, even though they remain separately selectable. Each block is numbered as one
// "Segment", with the individual pieces of a multi-segment block labeled by their position within it.
const getSegmentDisplayInfo = (segments: CameraDeviceSegment[]): SegmentDisplayInfo[] => {
    const mergeFlags = segments.map((segment, i) => {
        const previousSegment = segments[i - 1];
        const nextSegment = segments[i + 1];
        return {
            mergesWithPrevious: previousSegment !== undefined && moment(previousSegment.endTime).isSame(moment(segment.startTime)),
            mergesWithNext: nextSegment !== undefined && moment(segment.endTime).isSame(moment(nextSegment.startTime)),
        };
    });

    const displayInfo: SegmentDisplayInfo[] = [];
    let groupStart = 0;
    let groupNumber = 0;
    for (let i = 0; i < mergeFlags.length; i++) {
        if (i === mergeFlags.length - 1 || !mergeFlags[i + 1].mergesWithPrevious) {
            groupNumber++;
            const groupSize = i - groupStart + 1;
            for (let j = groupStart; j <= i; j++) {
                displayInfo.push({ ...mergeFlags[j], groupNumber, positionInGroup: j - groupStart + 1, groupSize });
            }
            groupStart = i + 1;
        }
    }

    return displayInfo;
};

const FootageSegments = (props: FootageSegmentsProps) => {
    const { date, pairing, viewState, availableSegments } = props;

    const handleSearchClick = async () => {
        props.onViewStateChange('searching');

        const serialNumber = pairing.cameraDevice?.serialNumber;
        if (!serialNumber) {
            props.onViewStateChange('no-footage');
            return;
        }

        try {
            await DeviceAssociationService.getFootageSegments(serialNumber, date);
        } catch {
            props.onViewStateChange('no-footage');
            return;
        }

        const segments = DeviceAssociationService.getState().cameraFootageSegments.data?.segments ?? [];

        if (segments.length === 0) {
            props.onViewStateChange('no-footage');
            return;
        }

        props.onSegmentsChange(segments);
        props.onViewStateChange('results');
    };

    const SearchBanner = () => (
        <div className={styles.container}>
            {isEncompass && (
                <div>
                    <FontAwesomeIcon icon={faInfoCircle} color={'#7F4313'} />
                </div>
            )}
            <div className={styles.descriptionAndTitleWrapper}>
                <div className={styles.title}>Unsure of the Time to recall?</div>
                <div className={styles.description}>Let our system search for available footage for the date selected.</div>
            </div>
            <div>
                <VPButton
                    variant="outlined"
                    text="Search"
                    onClick={handleSearchClick}
                    colorOverrides={{
                        color: isEncompass ? '#7F4313' : '#935E15',
                        borderColor: isEncompass ? '#7F4313' : '#935E15',
                    }}
                    hoverColorOverrides={{
                        color: isEncompass ? '#7F4313' : '#935E15',
                        borderColor: isEncompass ? '#7F4313' : '#935E15',
                        backgroundColor: isEncompass ? 'rgba(127, 67, 19, 0.08)' : 'rgba(147, 94, 21, 0.08)',
                    }}
                />
            </div>
        </div>
    );

    const SearchLoadingBanner = () => (
        <div className={styles.loadingContainer}>
            <div>
                <FontAwesomeIcon icon={faSpinner} spin />
            </div>
            <div className={styles.title}>Searching for available footage ...</div>
        </div>
    );

    const NoFootageBanner = () => (
        <div className={styles.noFootageContainer}>
            <div>
                <FontAwesomeIcon icon={faVideoSlash} />
            </div>
            <div className={styles.title}>No available footage found for selected date.</div>
        </div>
    );

    const AvailableFootageSegments = () => {
        const segmentDisplayInfo = getSegmentDisplayInfo(availableSegments);

        return (
            <div className={styles.availableFootageContainer}>
                <div className={styles.titleAvailableSegments}>Available Footage Segments</div>
                {availableSegments.map((segment, i) => {
                    const { mergesWithPrevious, mergesWithNext, groupNumber, positionInGroup, groupSize } = segmentDisplayInfo[i];
                    const isLast = i === availableSegments.length - 1;
                    const segmentName = `Segment ${groupNumber}`;
                    const segmentGroupPosition = groupSize > 1 ? ` (${positionInGroup}/${groupSize})` : '';

                    return (
                        <div
                            className={classNames(styles.segmentItem, {
                                [styles.segmentItemMergesWithPrevious]: mergesWithPrevious,
                                [styles.segmentItemMergesWithNext]: mergesWithNext,
                                [styles.segmentItemSpacedBelow]: !mergesWithNext && !isLast,
                            })}
                            key={i}
                            onClick={() => {
                                const serialNumber = pairing.cameraDevice?.serialNumber ?? '';
                                const groupSegments: CameraSegmentBase[] = availableSegments
                                    .map((s, j) => ({ s, info: segmentDisplayInfo[j] }))
                                    .filter(({ info }) => info.groupNumber === groupNumber)
                                    .map(({ s, info }) => ({
                                        ...s,
                                        serialNumber,
                                        segmentTitle: `Segment ${info.groupNumber}${info.groupSize > 1 ? ` (${info.positionInGroup}/${info.groupSize})` : ''}`,
                                    }));
                                props.onSegmentSelected?.({
                                    ...segment,
                                    serialNumber,
                                    segmentTitle: `${segmentName}${segmentGroupPosition}`,
                                    groupSegments,
                                });
                            }}
                        >
                            <div className={styles.segmentName}>
                                {segmentName}
                                {groupSize > 1 && <span className={styles.segmentGroupPosition}>{segmentGroupPosition}</span>}
                            </div>
                            <div
                                className={styles.segmentTime}
                            >{`${DateFormatter.getTime(moment(segment.startTime))} - ${DateFormatter.timeWithTimezone(moment(segment.endTime))}`}</div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderContent = () => {
        if (viewState === 'searching') return <SearchLoadingBanner />;
        if (viewState === 'no-footage') return <NoFootageBanner />;
        if (viewState === 'results') return <AvailableFootageSegments />;
        return <SearchBanner />;
    };

    return <div className={isEncompass ? styles.encompass : styles.geotab}>{renderContent()}</div>;
};

export default FootageSegments;
